import classNames from 'classnames';
import type { HTMLAttributes } from 'react';
import React, { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import FloatingArrow from './FloatingArrow';
import * as styles from './FloatingCard.module.scss';
import { useTetherContext } from './Tether';

interface FloatingCardProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  noArrow?: boolean;
}

const FloatingCard = forwardRef<HTMLDivElement, FloatingCardProps>(
  ({ children, noArrow, className, ...props }, ref) => {
    const { setArrow, arrowData, placement } = useTetherContext();

    const contentRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(true);
    useLayoutEffect(() => {
      const content = contentRef.current;
      if (!content) return undefined;

      const observer = new ResizeObserver((entries) => {
        const hasLoaded = entries.some(
          (entry) => entry.contentRect.width * entry.contentRect.height > 0,
        );
        if (hasLoaded) {
          observer.disconnect();
          setIsLoading(false);
        }
      });

      observer.observe(content);
      return () => observer.disconnect();
    }, []);

    return (
      <div
        ref={ref}
        className={classNames(styles.container, className, {
          [styles.isLoading]: isLoading,
        })}
        {...props}
      >
        <div ref={contentRef} className={styles.content}>
          {children}
        </div>
        <div className={styles.safeHoverArea} data-placement={placement} />
        {noArrow ? (
          <div className={styles.spacePlaceholder}>
            <FloatingArrow
              ref={setArrow}
              placement={placement}
              arrowData={arrowData}
              height={4}
              width={0}
            />
          </div>
        ) : (
          <FloatingArrow
            ref={setArrow}
            placement={placement}
            arrowData={arrowData}
            padding={2}
          />
        )}
      </div>
    );
  },
);

export default FloatingCard;
